import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowDown,
  Search,
  Sparkles,
  ClipboardList,
  Handshake,
  ShieldCheck,
  Wallet,
  Repeat2,
  Truck,
  Clock,
  BadgeCheck,
  MessageCircle,
  Car,
} from "lucide-react";
import Seo from "@/components/site/Seo";
import { useSettings } from "@/lib/settings";
import { FUELS, TRANSMISSIONS, BODY_TYPES } from "@/lib/format";
import { toast } from "sonner";

const STEPS = [
  {
    n: "01",
    icon: ClipboardList,
    title: "Completezi cererea",
    desc: "Ne spui ce autoturism îți dorești, cu detaliile relevante — marcă, model, buget.",
  },
  {
    n: "02",
    icon: Search,
    title: "Noi căutăm mașina",
    desc: "Cercetăm stocul partenerilor noștri din România și Europa pentru cea mai bună variantă.",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Primești ofertele",
    desc: "Îți trimitem cele mai bune opțiuni disponibile, cu poze, specificații și prețuri.",
  },
  {
    n: "04",
    icon: Handshake,
    title: "Alegi varianta ta",
    desc: "Alegi mașina care ți se potrivește, iar noi ne ocupăm de tot procesul până la livrare.",
  },
];

const ADVANTAGES = [
  { icon: Search, text: "Căutăm mașina pentru tine" },
  { icon: Clock, text: "Economisești timp" },
  { icon: BadgeCheck, text: "Istoric verificat" },
  { icon: Wallet, text: "Posibilitate finanțare" },
  { icon: Repeat2, text: "Buy Back disponibil" },
  { icon: MessageCircle, text: "Consultanță gratuită" },
  { icon: ShieldCheck, text: "Verificare tehnică" },
  { icon: Truck, text: "Livrare în toată țara" },
];

const REQUIRED = ["name", "phone", "brand", "model", "budget"];

const LABELS = {
  name: "Nume",
  phone: "Telefon",
  email: "Email",
  brand: "Marca",
  model: "Model",
  year_min: "An minim",
  year_max: "An maxim",
  budget: "Buget",
  fuel: "Combustibil",
  transmission: "Cutie",
  body_type: "Caroserie",
  km_max: "Kilometraj maxim",
  color: "Culoare",
  features: "Dotări dorite",
  notes: "Alte cerințe",
};

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  brand: "",
  model: "",
  year_min: "",
  year_max: "",
  budget: "",
  fuel: "",
  transmission: "",
  body_type: "",
  km_max: "",
  color: "",
  features: "",
  notes: "",
};

export default function OrderCar() {
  const { settings } = useSettings();
  const formRef = useRef(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: null }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const buildMessage = (f) => {
    const line = (label, val) => (val ? `${label}: ${val}` : null);
    const lines = [
      "Salut!",
      'Sunt interesat de serviciul "Mașini la comandă".',
      "",
      line("Nume", f.name),
      line("Telefon", f.phone),
      line("Email", f.email),
      line("Marca", f.brand),
      line("Model", f.model),
      line("An minim", f.year_min),
      line("An maxim", f.year_max),
      line("Buget", f.budget ? `${f.budget} €` : ""),
      line("Combustibil", f.fuel),
      line("Cutie", f.transmission),
      line("Caroserie", f.body_type),
      line("Kilometraj maxim", f.km_max ? `${f.km_max} km` : ""),
      line("Culoare", f.color),
      line("Dotări dorite", f.features),
      line("Alte cerințe", f.notes),
      "",
      "Aștept oferta dumneavoastră.",
      "Mulțumesc!",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submit = (e) => {
    e.preventDefault();
    const nextErr = {};
    for (const k of REQUIRED) {
      if (!String(form[k] || "").trim()) nextErr[k] = "Câmp obligatoriu";
    }
    if (Object.keys(nextErr).length > 0) {
      setErrors(nextErr);
      toast.error("Te rugăm completează câmpurile obligatorii.");
      const firstBadKey = REQUIRED.find((k) => nextErr[k]);
      const el = document.querySelector(`[name="${firstBadKey}"]`);
      el?.focus();
      return;
    }
    const msg = encodeURIComponent(buildMessage(form));
    const wa = (settings.whatsapp || "0753567030").replace(/\s/g, "");
    const num = wa.startsWith("40") ? wa : `40${wa.replace(/^0/, "")}`;
    const url = `https://wa.me/${num}?text=${msg}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Cererea ta este pregătită în WhatsApp — apasă Trimite acolo.");
  };

  return (
    <div className="bg-[#0a0a0a] min-h-screen pb-24">
      <Seo
        title="Mașini la comandă · AutoRIO Suceava"
        description="Nu găsești mașina dorită? AutoRIO caută autoturismul potrivit pentru tine, prin partenerii din România și Europa. Completează cererea și primești oferta prin WhatsApp."
        path="/masini-la-comanda"
      />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 30% 40%, rgba(212,175,55,0.18) 0%, transparent 60%), radial-gradient(80% 60% at 100% 100%, rgba(212,175,55,0.06) 0%, transparent 60%)",
          }}
        />
        <div className="relative container-wide grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-white/[0.05] backdrop-blur-md px-4 py-1.5 border border-white/[0.1]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/80">
                Serviciu personalizat
              </span>
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[72px] font-extrabold text-white leading-[0.98] tracking-tight">
              Nu găsești mașina dorită?
              <br />
              <span className="font-serif-italic gold-gradient-text font-medium">
                Noi
              </span>{" "}
              o aducem pentru tine.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
              Spune-ne ce autoturism îți dorești, iar noi căutăm cea mai bună
              variantă prin partenerii noștri din România și Europa.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                onClick={scrollToForm}
                className="gold-shine inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.55)]"
              >
                Completează cererea
                <ArrowDown className="h-4 w-4" />
              </button>
              <a
                href={`https://wa.me/40${(settings.whatsapp || "0753567030")
                  .replace(/\s/g, "")
                  .replace(/^0/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-transparent border border-white/25 hover:border-[#25D366] hover:text-[#25D366] text-white rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Scrie-ne acum
              </a>
            </div>
          </motion.div>

          {/* Right visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            className="hidden lg:flex lg:col-span-5 justify-end"
          >
            <div className="relative w-full max-w-[440px] aspect-square">
              {/* concentric rings */}
              <div className="absolute inset-0 rounded-full border border-[#D4AF37]/15" />
              <div className="absolute inset-8 rounded-full border border-[#D4AF37]/20" />
              <div className="absolute inset-16 rounded-full border border-[#D4AF37]/30 animate-pulse" />
              <div
                className="absolute inset-6 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle at 50% 40%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)",
                  filter: "blur(4px)",
                }}
              />
              {/* center car icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-3xl bg-[#D4AF37]/20 blur-2xl" />
                  <div className="relative h-28 w-28 rounded-3xl bg-gradient-to-br from-[#141210] to-[#0a0a0a] border border-[#D4AF37]/40 flex items-center justify-center shadow-[0_20px_60px_-10px_rgba(212,175,55,0.35)]">
                    <Car
                      className="h-14 w-14 text-[#D4AF37]"
                      strokeWidth={1.4}
                    />
                  </div>
                </div>
              </div>
              {/* orbit pills */}
              <FloatingPill
                icon={Search}
                label="Căutăm"
                className="top-4 left-2"
                delay={0.6}
              />
              <FloatingPill
                icon={BadgeCheck}
                label="Verificăm"
                className="top-8 right-0"
                delay={0.75}
              />
              <FloatingPill
                icon={Handshake}
                label="Livrăm"
                className="bottom-8 left-0"
                delay={0.9}
              />
              <FloatingPill
                icon={Sparkles}
                label="Ofertă"
                className="bottom-4 right-4"
                delay={1.05}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="container-wide py-16 sm:py-24 border-t border-white/[0.06]">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <div className="eyebrow">Procesul nostru</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Cum{" "}
            <span className="font-serif-italic gold-gradient-text font-medium">
              funcționează
            </span>
          </h2>
          <p className="mt-4 text-white/55 text-base sm:text-lg">
            Un proces simplu în 4 pași — de la cerere până la mașina în curte.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative rounded-2xl border border-white/[0.06] bg-[#111111] p-6 hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="h-12 w-12 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center">
                  <s.icon
                    className="h-5 w-5 text-[#D4AF37]"
                    strokeWidth={2.2}
                  />
                </span>
                <span
                  className="font-display text-[42px] font-extrabold leading-none tracking-tighter select-none"
                  style={{
                    WebkitTextStroke: "1.5px rgba(212,175,55,0.5)",
                    color: "transparent",
                  }}
                >
                  {s.n}
                </span>
              </div>
              <h3 className="font-display text-lg font-extrabold text-white leading-tight tracking-tight">
                {s.title}
              </h3>
              <p className="mt-2 text-[14px] text-white/55 leading-relaxed">
                {s.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------- ADVANTAGES ---------- */}
      <section className="container-wide py-16 sm:py-24 border-t border-white/[0.06]">
        <div className="relative rounded-3xl overflow-hidden border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#111] to-[#0a0a0a] p-8 sm:p-12">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#D4AF37]/12 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-48 w-48 rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="relative">
            <div className="eyebrow">Cu încredere</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              De ce să alegi{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                AutoRIO
              </span>
              ?
            </h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
              {ADVANTAGES.map((a, i) => (
                <motion.div
                  key={a.text}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-center gap-3"
                >
                  <span className="h-9 w-9 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                    <a.icon
                      className="h-4 w-4 text-[#D4AF37]"
                      strokeWidth={2.2}
                    />
                  </span>
                  <span className="text-white text-[14px] font-bold">
                    {a.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------- FORM ---------- */}
      <section
        ref={formRef}
        className="container-wide pt-16 sm:pt-20 pb-20 sm:pb-24 border-t border-white/[0.06] scroll-mt-28"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className="max-w-2xl mb-8">
              <div className="eyebrow">Cererea ta</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
                Completează{" "}
                <span className="font-serif-italic gold-gradient-text font-medium">
                  cererea
                </span>
              </h2>
              <p className="mt-4 text-white/55 text-base sm:text-lg">
                Cu cât detaliile sunt mai clare, cu atât oferta noastră va fi
                mai potrivită. Trimiterea se face direct prin WhatsApp.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="rounded-3xl border border-white/[0.06] bg-[#111111] p-6 sm:p-8 lg:p-10 shadow-[0_25px_60px_-25px_rgba(0,0,0,0.9)]"
              noValidate
            >
              {/* Contact block */}
              <SectionHeading n="01" title="Datele tale de contact" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                <Field
                  label={LABELS.name}
                  name="name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  error={errors.name}
                  placeholder="Prenume și nume"
                  autoComplete="name"
                />
                <Field
                  label={LABELS.phone}
                  name="phone"
                  required
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  error={errors.phone}
                  placeholder="07XX XXX XXX"
                  autoComplete="tel"
                />
              </div>
              <div className="mt-4">
                <Field
                  label={LABELS.email + " (opțional)"}
                  name="email"
                  type="email"
                  inputMode="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="nume@exemplu.ro"
                  autoComplete="email"
                />
              </div>

              {/* Car block */}
              <div className="mt-10">
                <SectionHeading n="02" title="Mașina dorită" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <Field
                    label={LABELS.brand}
                    name="brand"
                    required
                    value={form.brand}
                    onChange={set("brand")}
                    error={errors.brand}
                    placeholder="Ex: BMW"
                  />
                  <Field
                    label={LABELS.model}
                    name="model"
                    required
                    value={form.model}
                    onChange={set("model")}
                    error={errors.model}
                    placeholder="Ex: Seria 3"
                  />
                  <Field
                    label={LABELS.year_min}
                    name="year_min"
                    type="number"
                    inputMode="numeric"
                    value={form.year_min}
                    onChange={set("year_min")}
                    placeholder="2018"
                  />
                  <Field
                    label={LABELS.year_max}
                    name="year_max"
                    type="number"
                    inputMode="numeric"
                    value={form.year_max}
                    onChange={set("year_max")}
                    placeholder="2024"
                  />
                  <Field
                    label={LABELS.budget + " (€)"}
                    name="budget"
                    required
                    type="number"
                    inputMode="numeric"
                    value={form.budget}
                    onChange={set("budget")}
                    error={errors.budget}
                    placeholder="Ex: 20000"
                  />
                  <Field
                    label={LABELS.km_max + " (km)"}
                    name="km_max"
                    type="number"
                    inputMode="numeric"
                    value={form.km_max}
                    onChange={set("km_max")}
                    placeholder="150000"
                  />
                  <SelectField
                    label={LABELS.fuel}
                    name="fuel"
                    value={form.fuel}
                    onChange={set("fuel")}
                    options={FUELS}
                    placeholder="Alege"
                  />
                  <SelectField
                    label={LABELS.transmission}
                    name="transmission"
                    value={form.transmission}
                    onChange={set("transmission")}
                    options={TRANSMISSIONS}
                    placeholder="Alege"
                  />
                  <SelectField
                    label={LABELS.body_type}
                    name="body_type"
                    value={form.body_type}
                    onChange={set("body_type")}
                    options={BODY_TYPES}
                    placeholder="Alege"
                  />
                  <Field
                    label={LABELS.color + " (opțional)"}
                    name="color"
                    value={form.color}
                    onChange={set("color")}
                    placeholder="Ex: Negru, Alb, Gri"
                  />
                </div>
              </div>

              {/* Details block */}
              <div className="mt-10">
                <SectionHeading n="03" title="Detalii suplimentare" />
                <div className="grid grid-cols-1 gap-4 mt-5">
                  <TextareaField
                    label={LABELS.features + " (opțional)"}
                    name="features"
                    value={form.features}
                    onChange={set("features")}
                    placeholder="Ex: navigație, piele, cameră marșarier, faruri LED..."
                    rows={3}
                  />
                  <TextareaField
                    label={LABELS.notes + " (opțional)"}
                    name="notes"
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Alte preferințe sau observații"
                    rows={3}
                  />
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/[0.06]">
                <button
                  type="submit"
                  data-testid="order-car-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e8c86a] text-[#0a0a0a] rounded-full h-14 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.55)]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Trimite cererea prin WhatsApp
                </button>
                <p className="mt-4 text-center text-[12px] text-white/45">
                  Cererea se deschide automat în WhatsApp cu toate detaliile
                  completate. Doar apeși{" "}
                  <span className="text-[#D4AF37] font-bold">Trimite</span>{" "}
                  acolo.
                </p>
              </div>
            </form>
          </div>

          {/* Sticky helper card */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] p-6">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle className="h-4 w-4 text-[#25D366]" />
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">
                  Cum funcționează
                </div>
              </div>
              <ul className="space-y-3 text-[14px] text-white/80">
                <StepMini>
                  Completezi câmpurile obligatorii (nume, telefon, marcă,
                  model, buget).
                </StepMini>
                <StepMini>
                  Apeși butonul de trimitere — se deschide WhatsApp cu mesajul
                  pre-completat.
                </StepMini>
                <StepMini>
                  Apeși{" "}
                  <span className="text-[#D4AF37] font-bold">Trimite</span> în
                  WhatsApp și primești răspuns în cel mai scurt timp.
                </StepMini>
              </ul>
              <div className="mt-6 pt-6 border-t border-white/[0.06] text-[12px] text-white/50 leading-relaxed">
                Cererea ta{" "}
                <span className="text-white font-semibold">
                  nu este trimisă pe email
                </span>
                . Comunicăm direct prin WhatsApp pentru viteză și claritate.
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="container-wide pb-16 sm:pb-20">
        <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] bg-gradient-to-br from-[#141210] via-[#0e0d0b] to-[#0a0a0a] px-6 py-14 sm:px-12 sm:py-20 text-center">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-[#D4AF37]/8 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <div className="eyebrow">Ultima ocazie</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Nu ai găsit mașina{" "}
              <span className="font-serif-italic gold-gradient-text font-medium">
                perfectă
              </span>
              ?
            </h2>
            <p className="mt-4 text-white/60 text-base sm:text-lg">
              Lasă-ne cererea ta și noi ne ocupăm de tot procesul.
            </p>
            <button
              onClick={scrollToForm}
              className="gold-shine mt-8 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0a0a0a] hover:bg-[#e8c86a] rounded-full px-8 py-4 font-bold text-[13px] uppercase tracking-[0.18em] transition-colors shadow-[0_16px_50px_-12px_rgba(212,175,55,0.5)]"
            >
              Completează cererea
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Sub-components ---------- */
function FloatingPill({ icon: Icon, label, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      className={`absolute inline-flex items-center gap-2 rounded-full bg-[#0f0f0f]/85 backdrop-blur-md border border-white/[0.08] px-3.5 py-1.5 text-[11px] font-bold text-white ${className}`}
    >
      <Icon className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={2.4} />
      {label}
    </motion.div>
  );
}

function SectionHeading({ n, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-8 min-w-8 px-2 rounded-lg bg-[#D4AF37]/12 border border-[#D4AF37]/30 text-[#D4AF37] font-display font-extrabold text-[13px] flex items-center justify-center">
        {n}
      </span>
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">
        {title}
      </div>
    </div>
  );
}

function StepMini({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />
      <span>{children}</span>
    </li>
  );
}

const inpBase =
  "bg-[#0a0a0a] border rounded-lg h-12 px-4 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors";

function Field({
  label,
  name,
  required = false,
  error,
  className = "",
  ...rest
}) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-bold uppercase tracking-[0.16em] mb-2">
        <span className={error ? "text-red-400" : "text-white/60"}>
          {label}
          {required && <span className="text-[#D4AF37] ml-0.5">*</span>}
        </span>
      </label>
      <input
        name={name}
        data-testid={`order-${name}`}
        className={`${inpBase} ${
          error
            ? "border-red-500/60 focus:border-red-500"
            : "border-white/[0.08]"
        }`}
        {...rest}
      />
      {error && (
        <div className="mt-1.5 text-[12px] font-semibold text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}

function SelectField({ label, name, options, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-[0.16em] text-white/60 mb-2">
        {label}
      </label>
      <select
        name={name}
        data-testid={`order-${name}`}
        value={value}
        onChange={onChange}
        className={`${inpBase} border-white/[0.08]`}
      >
        <option value="">{placeholder || "Alege"}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function TextareaField({ label, name, rows = 3, ...rest }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-[0.16em] text-white/60 mb-2">
        {label}
      </label>
      <textarea
        name={name}
        data-testid={`order-${name}`}
        rows={rows}
        className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-4 py-3 w-full text-white placeholder-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 text-[14px] outline-none transition-colors resize-none"
        {...rest}
      />
    </div>
  );
}
